/**
 * ErrorBoundary — catches any uncaught render error anywhere below it
 * and shows a friendly fallback UI instead of a blank white screen.
 *
 * Reports the full stack trace to /api/client-errors so the operator can
 * review broken flows in the /admin/error-log dashboard. Gives the user
 * a "Reload" button + a "Report to team" copy.
 *
 * Every layout/route in the app should be mounted below this at least
 * once — see App.js where <ErrorBoundary> wraps the whole <Routes> tree.
 */
import { Component } from "react";
import { Sentry } from "@/instrumentation";

const API = process.env.REACT_APP_BACKEND_URL;

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null, reported: false };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    // Auto-heal: a "Loading chunk" or "Failed to fetch dynamically
    // imported module" almost always means a deployed build shipped a
    // new hash and the tab has stale HTML. Force a one-shot reload so
    // the user sees a working app instead of the fallback UI.
    // Guarded by a sessionStorage flag so we never loop.
    try {
      const msg = String(error?.message || error || "");
      const isChunkErr = /Loading chunk|ChunkLoadError|failed to fetch dynamically imported module|Importing a module script failed/i.test(msg);
      if (isChunkErr && !sessionStorage.getItem("crc_autoheal_reload")) {
        sessionStorage.setItem("crc_autoheal_reload", "1");
        window.location.reload();
        return;
      }
    } catch { /* noop */ }
    // Fire-and-forget report — never blocks the fallback UI.
    // Sends to BOTH the legacy client-errors endpoint AND the new
    // auto-healing rollup so the 24/7 healer can auto-fix common cases.
    try {
      const errPayload = {
        kind: "render-error",
        message: String(error?.message || error || "unknown").slice(0, 500),
        stack: String(error?.stack || "").slice(0, 2000),
        url: window.location.href,
        user_agent: navigator.userAgent.slice(0, 200),
      };
      fetch(`${API}/api/auto-healing/errors/report`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(errPayload),
      }).catch(() => {});
      fetch(`${API}/api/client-errors`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("crc_session_token") || ""}`,
        },
        body: JSON.stringify({
          kind: "react_error_boundary",
          message: String(error?.message || error || "unknown"),
          stack: String(error?.stack || "").slice(0, 4000),
          component_stack: String(info?.componentStack || "").slice(0, 4000),
          path: window.location.pathname + window.location.search,
          user_agent: navigator.userAgent,
        }),
      }).then(() => this.setState({ reported: true })).catch(() => {});
    } catch { /* noop */ }
    // Also send to Sentry so ops sees it in the observability dashboard
    // (with a LogRocket session URL linked via beforeSend).
    try {
      Sentry.withScope((scope) => {
        scope.setTag("source", "react_error_boundary");
        scope.setExtra("component_stack", String(info?.componentStack || "").slice(0, 4000));
        Sentry.captureException(error);
      });
    } catch { /* noop */ }
    // Also surface in the dev console so pull-request reviews see it.
    console.error("Uncaught render error:", error, info);
  }

  reset = () => this.setState({ error: null, info: null, reported: false });
  reload = () => {
    // Hard-reload with a cache-busting query so service-worker caches
    // (PWA) can't re-serve the broken bundle that put us in the error
    // boundary. Falls back to a plain reload if the location is opaque.
    try {
      const u = new URL(window.location.href);
      u.searchParams.set("_r", Date.now().toString(36));
      window.location.replace(u.toString());
    } catch {
      window.location.reload();
    }
  };
  goHome = () => { window.location.assign("/"); };

  render() {
    if (!this.state.error) return this.props.children;
    const short = String(this.state.error?.message || this.state.error || "Something went wrong.");
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="error-boundary">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg border border-red-200 p-8">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
            <span className="text-red-600 text-2xl font-bold">!</span>
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Something went wrong</h1>
          <p className="text-sm text-slate-600 mb-4">
            The page hit an unexpected error. We&apos;ve auto-reported it to
            the Central Rentals team — you don&apos;t need to do anything.
          </p>
          <details className="text-xs text-slate-500 mb-6 bg-slate-50 rounded p-2 border border-slate-200">
            <summary className="cursor-pointer font-medium">Technical details</summary>
            <div className="mt-2 font-mono break-all whitespace-pre-wrap max-h-32 overflow-y-auto">{short}</div>
            {this.state.reported && <div className="mt-1 text-emerald-700">✓ Reported to team</div>}
          </details>
          <div className="flex gap-2">
            <button
              onClick={this.reload}
              data-testid="error-reload-btn"
              className="flex-1 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm"
            >
              Reload page
            </button>
            <button
              onClick={this.goHome}
              data-testid="error-home-btn"
              className="flex-1 py-2.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold text-sm"
            >
              Back to home
            </button>
          </div>
        </div>
      </div>
    );
  }
}
